import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';
import type { BlockRecord } from '../blocks/types';

import type { ImportMappingInput, ImportTable } from '@couplespace/core/databases';

import type {
  BlockPropertyRecord,
  DatabasePageInfo,
  DatabaseQueryInput,
  DatabaseQueryResult,
  DatabaseSchemaRecord,
  UpdateDatabaseSchemaInput,
} from './types';

export type ImportDatabaseInput = {
  table: ImportTable;
  mapping?: ImportMappingInput;
};

export type ImportDatabaseResult = {
  rowsInserted: number;
  columnsAdded: number;
  propertiesInserted: number;
  optionsCreated: number;
};

function networkFetchError(base: string, err: unknown): Error {
  const msg = err instanceof Error ? err.message : 'Network error';
  return new Error(`Cannot reach API at ${base} (${msg}).`);
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  try {
    return await fetch(`${base}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getDevAuthHeaders(),
        ...(await getNativeBearerAuthHeaders()),
        ...init?.headers,
      },
    });
  } catch (err) {
    throw networkFetchError(base, err);
  }
}

/** Drop preview-only fields so large CSV imports stay under proxy body limits. */
function slimImportTableForApi(table: ImportTable): ImportTable {
  return {
    name: table.name,
    columns: table.columns.map(({ sourceName, inferredType }) => ({
      sourceName,
      inferredType,
      sampleValues: [],
    })),
    rows: table.rows,
  };
}

function formatApiError(status: number, body: string): string {
  try {
    const parsed = JSON.parse(body) as {
      error?: { message?: string; issues?: { message?: string; path?: (string | number)[] }[] };
    };
    const issues = parsed.error?.issues;
    if (issues?.length) {
      return issues.map((i) => i.message ?? 'Validation error').join('; ');
    }
    if (parsed.error?.message) {
      return parsed.error.message;
    }
  } catch {
    /* not JSON */
  }
  return body.trim() || `Request failed (${status})`;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(formatApiError(res.status, text));
  }
  return res.json() as Promise<T>;
}

export async function fetchDatabaseSchema(databaseId: string): Promise<DatabaseSchemaRecord> {
  const res = await apiFetch(`/rpc/databases/${databaseId}/schema`);
  const body = await parseJson<{ schema: DatabaseSchemaRecord }>(res);
  return body.schema;
}

export async function fetchDatabaseRows(
  databaseId: string,
  options?: { limit?: number; cursor?: string },
): Promise<{ rows: BlockRecord[]; pageInfo: DatabasePageInfo }> {
  const params = new URLSearchParams();
  if (options?.limit != null) {
    params.set('limit', String(options.limit));
  }
  if (options?.cursor) {
    params.set('cursor', options.cursor);
  }
  const q = params.toString();
  const res = await apiFetch(`/rpc/databases/${databaseId}/rows${q ? `?${q}` : ''}`);
  return parseJson(res);
}

export async function queryDatabase(
  databaseId: string,
  body: DatabaseQueryInput,
): Promise<DatabaseQueryResult> {
  const res = await apiFetch(`/rpc/databases/${databaseId}/query`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

export async function createDatabaseRow(
  databaseId: string,
  input?: { title?: string; position?: number },
): Promise<BlockRecord> {
  const res = await apiFetch(`/rpc/databases/${databaseId}/rows`, {
    method: 'POST',
    body: JSON.stringify(input ?? {}),
  });
  const data = await parseJson<{ row: BlockRecord }>(res);
  return data.row;
}

export async function fetchRowProperties(rowId: string): Promise<BlockPropertyRecord[]> {
  const res = await apiFetch(`/rpc/blocks/${rowId}/properties`);
  const data = await parseJson<{ properties: BlockPropertyRecord[] }>(res);
  return data.properties;
}

export async function upsertRowProperty(
  rowId: string,
  propertyId: string,
  value: Record<string, unknown>,
  options?: { expectedSyncVersion?: number },
): Promise<BlockPropertyRecord> {
  const res = await apiFetch(`/rpc/blocks/${rowId}/properties/${propertyId}`, {
    method: 'PUT',
    body: JSON.stringify({
      value,
      expectedSyncVersion: options?.expectedSyncVersion,
    }),
  });
  const data = await parseJson<{ property: BlockPropertyRecord }>(res);
  return data.property;
}

export async function updateDatabaseSchema(
  databaseId: string,
  input: UpdateDatabaseSchemaInput,
): Promise<DatabaseSchemaRecord> {
  const res = await apiFetch(`/rpc/databases/${databaseId}/schema`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  const data = await parseJson<{ schema: DatabaseSchemaRecord }>(res);
  return data.schema;
}

export async function importDatabase(
  databaseId: string,
  input: ImportDatabaseInput,
): Promise<ImportDatabaseResult> {
  const payload: ImportDatabaseInput = {
    mapping: input.mapping,
    table: slimImportTableForApi(input.table),
  };
  const res = await apiFetch(`/rpc/databases/${databaseId}/import`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(10 * 60 * 1000),
  });
  return parseJson(res);
}

export type DatabaseExportFormat = 'csv' | 'json' | 'markdown';

export async function downloadDatabaseExport(
  databaseId: string,
  options: {
    format: DatabaseExportFormat;
    search?: string;
    filter?: DatabaseQueryInput['filter'];
    sort?: DatabaseQueryInput['sort'];
  },
): Promise<void> {
  const params = new URLSearchParams();
  params.set('format', options.format);
  if (options.search) {
    params.set('search', options.search);
  }
  if (options.filter?.length) {
    params.set('filter', JSON.stringify(options.filter));
  }
  if (options.sort?.length) {
    params.set('sort', JSON.stringify(options.sort));
  }
  let query = params.toString();
  const maxQueryLen = 6000;
  if (query.length > maxQueryLen) {
    params.delete('filter');
    params.delete('sort');
    query = params.toString();
  }
  const res = await apiFetch(`/rpc/databases/${databaseId}/export?${query}`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(formatApiError(res.status, text));
  }
  const blob = await res.blob();
  const ext =
    options.format === 'csv' ? 'csv' : options.format === 'json' ? 'json' : 'md';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `database-export.${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function duplicateDatabaseRow(
  databaseId: string,
  sourceRow: BlockRecord,
  sourceProperties: BlockPropertyRecord[],
): Promise<BlockRecord> {
  const copy = await createDatabaseRow(databaseId, {
    title: sourceRow.title ? `${sourceRow.title} (copy)` : 'Untitled (copy)',
    position: sourceRow.position + 0.5,
  });
  for (const prop of sourceProperties) {
    if (prop.value && typeof prop.value === 'object') {
      await upsertRowProperty(copy.id, prop.propertyId, prop.value);
    }
  }
  return copy;
}
